"use client";

import { absoluteCmsUrl, BaseImage } from "../base-image/BaseImage";
import React, { useEffect, useRef } from "react";
import { HeroImageProps } from "./types";

type HeroMediaProps = {
  media?: HeroImageProps["media"] | null;
  className?: string;
  imagePosition?: string | null;
};

export const HeroImage: React.FC<HeroMediaProps> = ({
  media,
  className,
  imagePosition,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const mediaMime = (media as any)?.mime as string | undefined;
  const isVideo =
    !!mediaMime?.startsWith("video/") ||
    /\.(mp4|webm|ogg)$/i.test(media?.url || "");

  const rawImageSrc =
    media?.url ||
    media?.formats?.large?.url ||
    media?.formats?.medium?.url ||
    media?.formats?.small?.url ||
    "";

  const src = absoluteCmsUrl(rawImageSrc);
  const poster = absoluteCmsUrl(
    media?.formats?.thumbnail?.url || media?.formats?.small?.url || "",
  );
  const alt =
    media?.alternativeText ||
    media?.caption ||
    media?.name ||
    (isVideo ? "Hero video" : "Hero image");

  useEffect(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => {
      if (m.matches) {
        v.pause();
        v.removeAttribute("autoplay");
        v.removeAttribute("loop");
      }
    };
    apply();
    try {
      m.addEventListener("change", apply);
    } catch {
      m.addListener(apply);
    }
    return () => {
      try {
        m.removeEventListener("change", apply);
      } catch {
        m.removeListener(apply);
      }
    };
  }, [isVideo]);

  if (!src) return null;

  return isVideo ? (
    <video
      ref={videoRef}
      className={className ?? "absolute inset-0 w-full h-full object-cover"}
      src={src}
      poster={poster || undefined}
      autoPlay
      loop
      muted
      playsInline
      preload="metadata"
      aria-label={alt}
      style={{ 
        aspectRatio: '16/9',
        objectFit: 'cover',
        objectPosition: 'center center'
      }}
    >
      <track
        kind="captions"
        srcLang="en"
        label="No captions available"
      />
    </video>
  ) : (
    <BaseImage
      src={src}
      alt={alt}
      fill
      sizes="(max-width: 1024px) 100vw, 90vw"
      priority
      fetchPriority="high"
      quality={90}
      imagePosition={imagePosition}
      className={className ?? "absolute inset-0 w-full h-full object-cover"}
    />
  );
};
